import {CtrDomainsVf, CtrInscriptions, Maybe} from "../graphql/generated/graphql-main.tsx";
import {prefix, removeAccents, strEmpty} from "../utils/utils.ts";
import useCtrMetaFields from "./useCtrMetaFields.tsx";
import useCtrGenresProprietes from "./useCtrGenresProprietes.ts";
import useCtrRegions from "./useCtrRegions.ts";
import useReactTexts from "./useReactTexts.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";

const obviousWaterBodyNonValues = [
    "aucun",
    "aucune",
    "municipalite",
    "municipality",
    "na",
    "nil",
    "no",
    "non",
    "none",
    "nonapplicable",
    "notapplicable",
    "null",
    "sansnom",
    "so",
];

/**
 * Miscellaneous helper functions related to any single listing (useful in both grid layout and single listing layout)
 */
export default function useCtrInscription() {
    const {is_fr} = useWpContext();
    const {__} = useReactTexts();
    const {toValue} = useCtrMetaFields();
    const {genreBy} = useCtrGenresProprietes();
    const {munName, qrtName, regNameByMun} = useCtrRegions();

    // whether this listing is for sale or rent (determined by presence of PRIX_DEMANDE vs PRIX_LOCATION_DEMANDE)
    const isForSale = (lst: Maybe<CtrInscriptions>) => {
        return (lst?.PRIX_DEMANDE != null && !strEmpty(lst?.PRIX_DEMANDE))
            && (lst?.PRIX_LOCATION_DEMANDE == null || strEmpty(lst?.PRIX_LOCATION_DEMANDE));
    }

    // custom property title per {@link CtrInscriptions} including type of property, sale/rent, and municipality
    const title = (lst: Maybe<CtrInscriptions>) => {
        const value = toValue<CtrInscriptions>(lst);
        const type = value("GENRE_PROPRIETE", {
            getter: val => genreBy(lst?.CATEGORIE_PROPRIETE ?? null, val) ?? "nd"
        });
        const saleOrRent = isForSale(lst) ? __("à vendre") : __("à louer");
        return `${type} ${saleOrRent} - ${munName(lst?.MUN_CODE)}`;
    }

    /**
     * Display-safe water body name from the free-form Centris field.
     * The blacklist only removes clear non-values; vague water hints still carry property context.
     * The returned value also guarantees an uppercase first letter for title display without rewriting the rest.
     */
    const waterBodyName = (lst: Maybe<CtrInscriptions>): Maybe<string> => {
        const value = lst?.NOM_PLAN_EAU?.trim().replace(/\s+/g, " ") ?? null;
        if (strEmpty(value)) return null;

        const normalized = removeAccents(value!).toLowerCase();
        const compact = normalized.replace(/[\s./_-]+/g, "");
        if (obviousWaterBodyNonValues.includes(compact)) return null;

        // Free-form fields sometimes contain placeholders that are not words at all.
        if (/^[\W_]+$/u.test(value!) || /^[\d\s.,-]+$/.test(value!)) return null;

        return value!.at(0)?.toLocaleUpperCase(is_fr ? "fr-CA" : "en-CA").concat(value!.slice(1)) ?? value;
    }

    /**
     * Listing title variant for the single-listing page.
     * Keeps the existing title intact and appends the water body only when the field is meaningful.
     */
    const titleWithWaterBody = (lst: Maybe<CtrInscriptions>) => {
        return [title(lst), waterBodyName(lst)].filter(Boolean).join(" - ");
    }

    // return price tag for use in Google Maps pins (no symbols, only raw number w/ K or M as range indicator);
    // https://stackoverflow.com/questions/9461621/format-a-number-as-2-5k-if-a-thousand-or-more-otherwise-900
    const shortPrice = (lst: Maybe<CtrInscriptions>) => {
        const lookup: Array<{ value: number; symbol: string }> = [
            {value: 0, symbol: ""}, // though super rare, some land lots are at $0 :)
            {value: 1, symbol: ""}, // never 0 to avoid Infinity or division-by-zero
            {value: 1e3, symbol: "K"},
            {value: 1e6, symbol: "M"}
        ];
        // note: though property may have both rental & full price, ONLY show either price in map pin, not both
        const _price = parseFloat(lst?.PRIX_DEMANDE ?? lst?.PRIX_LOCATION_DEMANDE ?? "0") || 0;
        const range = lookup.findLast(it => _price >= it.value)!;
        const rangeValue = range?.value || 1; // price bracket against which to divide
        const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/; // remove any trailing zeroes (e.g. 750.00K, 1.40M, etc.)
        return (_price / rangeValue).toFixed(2).replace(regexp, "").concat(range.symbol);
    }

    /**
     * Given a listing, return proper pricing info to display (includes rental price, etc.).
     * @param lst
     * @param asText - return price as string instead of HTML
     */
    const price = (lst: Maybe<CtrInscriptions>, asText?: boolean) => {
        const value = toValue<CtrInscriptions>(lst);
        const _p1 = value("PRIX_DEMANDE", {type: "currency"});
        const _p2 = value("PRIX_LOCATION_DEMANDE", {type: "currency"});
        // check if property price should display taxes
        const _tx1 = lst?.IND_TAXES_PRIX_DEMANDE == 'O';
        const _tx2 = lst?.IND_TAXES_PRIX_LOCATION_DEMANDE == 'O';

        let _suffix1 = _tx1 ? ' +taxes' : '';
        let _suffix2 = '';
        if (_p2 != null && !strEmpty(_p2)) {
            // show per unit of surface
            const _unit = prefix(" / ", value("UM_PRIX_LOCATION_DEMANDE", {
                domain: CtrDomainsVf.UmPrix
            }) ?? "").toLowerCase();

            // show per rental frequency
            const _frequency = prefix(" / ", value("FREQUENCE_PRIX_LOCATION", {
                domain: CtrDomainsVf.FrequencePrixLocation
            }) ?? "").toLowerCase();

            // either unit | frequency; else we get things like '$20.00 / square foot / year', which is weird
            _suffix2 = _unit || _frequency;
            _suffix2 += _tx2 ? ' +taxes' : '';
        }

        // some properties have both full price and rental prices, so show both prices separated by 'or'
        const prices = [];
        if (!strEmpty(_p1)) {
            prices.push(`${_p1} ${_suffix1}`.trim());
        }
        if (!strEmpty(_p2)) {
            prices.push(`${_p2} ${_suffix2}`.trim());
        }

        if (asText) {
            // in rare cases we may need to print prices as raw strings w/o HTML (optional, non-defaults)
            return prices.join(` ${__('ou')} `);
        } else {
            // to have more control over position of price, return as HTML instead of raw string
            return <>{prices.map((price, idx) => {
                const sep = idx < prices.length - 1 ? __('ou') : '';
                return <span key={`price-${lst?.NO_INSCRIPTION}-${idx}`} className={"price"}>
                    {price}&nbsp;{sep}&nbsp;
                </span>
            })}</>;
        }
    }

    // Get formatted street number from given listing; dynamically add '-' between parts of the street number.
    const streetNumber = (lst: Maybe<CtrInscriptions>) => {
        return [lst?.NO_CIVIQUE_DEBUT, lst?.NO_CIVIQUE_FIN]
            .filter(it => it != null && !strEmpty(it))
            .join(" - ");
    }

    // reduced address w/o the municipality and region (only street number, name, and apartment)
    const shortAddress = (lst: Maybe<CtrInscriptions>) => {
        const _address = `${streetNumber(lst)} ${lst?.NOM_RUE_COMPLET}`;
        const _apart = prefix(', #', lst?.APPARTEMENT!);
        const _quart = prefix(',', qrtName(lst?.MUN_CODE, lst?.QUARTR_CODE));
        return `${_address}${_apart}${_quart}`.trim();
    }

    /**
     * This address contains raw address coming from Centris w/o any custom region/municipality labels, etc.
     * Does NOT contain the apartment number since the raw address is used to identify listings from a McGill project.
     * E.g., we use it in grid view's context menu to copy full raw address to match w/ McGill project (e.g. SWL).
     * Note: raw address using original Centris data only (do NOT use custom municipality & region names, etc.).
     * Note: always identical to hub @ CentrisHelper::rawAddress
     * @param lst
     */
    const rawAddress = (lst: Maybe<CtrInscriptions>) => {
        const _address = `${streetNumber(lst)} ${lst?.NOM_RUE_COMPLET}`;
        const _mun = prefix(',', munName(lst?.MUN_CODE, {strict: true}));
        const _reg = prefix(',', regNameByMun(lst?.MUN_CODE, {strict: true}));
        return `${_address}${_mun}${_reg}`.trim();
    }

    /**
     * More loose address for display only. It includes apartment number, and may also show custom municipality
     * names from our dictionary, but we cannot rely on this address for matching McGill projects.
     * @param lst
     */
    const displayAddress = (lst: Maybe<CtrInscriptions>) => {
        const _address = `${streetNumber(lst)} ${lst?.NOM_RUE_COMPLET}`;
        const _apart = prefix(', #', lst?.APPARTEMENT!);
        const _mun = prefix(',', munName(lst?.MUN_CODE));
        const _qrt = prefix(',', qrtName(lst?.MUN_CODE, lst?.QUARTR_CODE));
        const _reg = prefix(',', regNameByMun(lst?.MUN_CODE));
        return `${_address}${_apart}${_mun}${_qrt}${_reg}`.trim();
    }

    return {
        isForSale,
        title,
        titleWithWaterBody,
        waterBodyName,
        price,
        shortPrice,
        shortAddress,
        rawAddress,
        displayAddress
    }
}
